import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { deliveryApi } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Check, ShoppingCart } from 'lucide-react';

const DeliveryProductConfigurator = ({ product }) => {
  const { t } = useTranslation();
  const { addItem, cart } = useCart();
  const [config, setConfig] = useState(null);
  const [zone, setZone] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [deliveryType, setDeliveryType] = useState('');
  const [urgent, setUrgent] = useState('standard');
  const [added, setAdded] = useState(false);

  const isLivraison = product.sku?.toLowerCase() === 'livd';
  const serviceKey = isLivraison ? 'delivery' : 'pickup';
  const serviceLabel = t(`delivery_config.${serviceKey}`);
  const alreadyInCart = cart.items.some(item => item.product_id === String(product.id));

  useEffect(() => {
    deliveryApi.getConfig().then(res => {
      const cfg = res.data;
      setConfig(cfg);
      if (cfg.zones?.length) setZone(cfg.zones[0].name);
      if (cfg.time_slots?.length) setTimeSlot(cfg.time_slots[0].label);
      if (cfg.delivery_types?.length) setDeliveryType(cfg.delivery_types[0].label);
    }).catch(() => {});
  }, []);

  if (!config) return null;

  const basePrice = product.daily_price || 65;
  const zoneSurcharge = config.zones?.find(z => z.name === zone)?.surcharge || 0;
  const isSurDevis = zoneSurcharge === -1;
  const timeSlotSurcharge = config.time_slots?.find(ts => ts.label === timeSlot)?.surcharge || 0;
  const deliveryTypeSurcharge = config.delivery_types?.find(dt => dt.label === deliveryType)?.surcharge || 0;
  const urgencySurcharge = urgent === 'urgent' ? (config.urgency_surcharge || 50) : 0;
  const totalPrice = isSurDevis ? null : basePrice + (zoneSurcharge > 0 ? zoneSurcharge : 0) + timeSlotSurcharge + deliveryTypeSurcharge + urgencySurcharge;

  const handleAddToCart = () => {
    if (alreadyInCart) return;
    addItem({
      product_id: String(product.id),
      product_name: `${product.name} — ${zone}, ${timeSlot}, ${deliveryType}${urgent === 'urgent' ? ', URGENTE' : ''}`,
      product_sku: product.sku,
      quantity: 1,
      daily_price: totalPrice || basePrice,
      image_url: product.image_url,
      is_delivery_service: true,
      delivery_options: { zone, time_slot: timeSlot, delivery_type: deliveryType, urgent: urgent === 'urgent' }
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="space-y-5" data-testid="delivery-configurator">
      <p className="text-sm text-muted-foreground">
        {t('delivery_config.configure_text', { service: serviceLabel })}
      </p>

      <div className="space-y-2">
        <Label className="font-semibold">{t('delivery_config.urgency')}</Label>
        <Select value={urgent} onValueChange={setUrgent}>
          <SelectTrigger className="rounded-none" data-testid="config-urgency"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="standard">{t('delivery_config.standard_detail')}</SelectItem>
            <SelectItem value="urgent">{t('delivery_config.urgent_detail', { price: config.urgency_surcharge || 50 })}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="font-semibold">{t('delivery_config.zone_label', { service: serviceLabel })}</Label>
        <Select value={zone} onValueChange={setZone}>
          <SelectTrigger className="rounded-none" data-testid="config-zone"><SelectValue /></SelectTrigger>
          <SelectContent>
            {config.zones?.map(z => (
              <SelectItem key={z.name} value={z.name}>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: z.color }} />
                  {z.name} : {z.surcharge === -1 ? t('delivery_config.on_quote') : `+${z.surcharge} ${t('common.eur_ht')}`}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="font-semibold">{t('delivery_config.drop_label')}</Label>
        <Select value={deliveryType} onValueChange={setDeliveryType}>
          <SelectTrigger className="rounded-none" data-testid="config-delivery-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {config.delivery_types?.map(dt => (
              <SelectItem key={dt.label} value={dt.label}>{dt.label} : +{dt.surcharge} {t('common.eur_ht')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="font-semibold">{t('delivery_config.time_label', { service: serviceLabel })}</Label>
        <Select value={timeSlot} onValueChange={setTimeSlot}>
          <SelectTrigger className="rounded-none" data-testid="config-timeslot"><SelectValue /></SelectTrigger>
          <SelectContent>
            {config.time_slots?.map(ts => (
              <SelectItem key={ts.label} value={ts.label}>{ts.label} : +{ts.surcharge} {t('common.eur_ht')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      <div className="bg-surface p-4 space-y-2" data-testid="config-price-summary">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{serviceLabel} ({t('delivery_config.base')})</span>
          <span>{basePrice.toFixed(2)} {t('common.eur_ht')}</span>
        </div>
        {zoneSurcharge > 0 && (
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{zone}</span><span>+{zoneSurcharge.toFixed(2)} {t('common.eur_ht')}</span></div>
        )}
        {isSurDevis && (
          <div className="flex justify-between text-sm text-orange-600"><span>{zone}</span><span>{t('delivery_config.on_quote')}</span></div>
        )}
        {timeSlotSurcharge > 0 && (
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{timeSlot}</span><span>+{timeSlotSurcharge.toFixed(2)} {t('common.eur_ht')}</span></div>
        )}
        {deliveryTypeSurcharge > 0 && (
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{deliveryType}</span><span>+{deliveryTypeSurcharge.toFixed(2)} {t('common.eur_ht')}</span></div>
        )}
        {urgencySurcharge > 0 && (
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t('delivery_config.urgency')}</span><span>+{urgencySurcharge.toFixed(2)} {t('common.eur_ht')}</span></div>
        )}
        <Separator />
        <div className="flex justify-between font-bold text-lg">
          <span>{t('delivery_config.total')}</span>
          <span className="text-primary" data-testid="config-total">
            {isSurDevis ? t('delivery_config.on_quote') : `${totalPrice.toFixed(2)} ${t('common.eur_ht')}`}
          </span>
        </div>
      </div>

      {alreadyInCart ? (
        <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 text-sm font-medium">
          <Check className="w-4 h-4" />
          {t('delivery_config.already_in_cart', { service: serviceLabel })}
        </div>
      ) : (
        <Button onClick={handleAddToCart} className="w-full rounded-none" disabled={isSurDevis || added} data-testid="config-add-to-cart">
          {added ? (
            <><Check className="mr-2 w-4 h-4" /> {t('delivery_config.added')}</>
          ) : isSurDevis ? (
            t('delivery_config.contact_for_quote')
          ) : (
            <><ShoppingCart className="mr-2 w-4 h-4" /> {t('delivery_config.add_to_cart')} — {totalPrice.toFixed(2)} {t('common.eur_ht')}</>
          )}
        </Button>
      )}
    </div>
  );
};

export default DeliveryProductConfigurator;
